import { useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import {
  createConnectQueryKey,
  useMutation,
  useQuery,
  useTransport,
} from '@connectrpc/connect-query'
import { Trans, useLingui } from '@lingui/react/macro'

import { useGetCustomer } from '@jotta/grpc-connect-client/customer'
import { ShareAccessLevel } from '@jotta/grpc-connect-openapi/album'
import {
  getAlbum,
  listSubscribers,
  shareAlbum as shareAlbumRequest,
  unshareAlbum as unshareAlbumRequest,
} from '@jotta/grpc-connect-openapi/albumQuery'
import { getCollection } from '@jotta/grpc-connect-openapi/collectionQuery'
import { queryClient } from '@jotta/query'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@jotta/ui/Dialog'
import { Divider } from '@jotta/ui/Divider'
import SvgLinkSimple from '@jotta/ui/SvgLinkSimple'
import SvgUsersShared from '@jotta/ui/SvgUsersShared'
import { showToast } from '@jotta/ui/ToastProvider'
import { createUrl } from '@jotta/utils/url'

import { OPTIMISTIC_SHARE_ID } from '../share.constants'

import { CopyToClipboardCard } from './internals/CopyToClipboardCard'
import { ToggleCard } from './internals/ToggleCard'
import { UnShareAlbumDialog } from './UnShareAlbumDialog'

export type ShareAlbumDialogProps = {
  albumId?: string
  disabled?: boolean
  onOpenChange: (open: boolean) => void
  triggerElement: React.ReactNode
  forceOpen?: boolean
}

export const ShareAlbumDialog: React.FC<ShareAlbumDialogProps> = ({
  disabled,
  onOpenChange,
  albumId,
  triggerElement,
  forceOpen,
}) => {
  const { t } = useLingui()
  const [open, setOpen] = useState(false)
  const [confirmUnshareOpen, setConfirmUnshareOpen] = useState(false)

  const { data: customer } = useGetCustomer()
  const { album, collection, shareAlbum, unshareAlbum } = useShareAlbum(albumId)

  const isShared = Boolean(album?.shareId)
  const dialogOpen = forceOpen ? true : open

  const checkUnshareImpact = useMutation(listSubscribers, {
    onSuccess: data => {
      if (!dialogOpen) {
        return
      }
      if (data.subscribers.length) {
        setConfirmUnshareOpen(true)
      } else {
        unshareAlbum.mutate({ albumId })
      }
    },
    onError: () => {
      if (!dialogOpen) {
        return
      }
      showToast({
        message: t`Something went wrong. Please try again.`,
        hasCloseButton: true,
        duration: 'short',
      })
    },
  })

  const albumCoverThumbUrl = collection?.coverPhoto?.expiringSmallThumbUrl

  const confirmedShareId =
    album?.shareId && album.shareId !== OPTIMISTIC_SHARE_ID
      ? album.shareId
      : undefined

  const shareLink = confirmedShareId
    ? createUrl('share', confirmedShareId)
    : undefined

  const isOwner = !!customer?.username && customer?.username === album?.owner

  const subscribersAlbumIdOrShareId = confirmedShareId ?? album?.id

  return (
    <Dialog
      modal={true}
      open={dialogOpen}
      onOpenChange={value => {
        setOpen(value)
        onOpenChange(value)
      }}
    >
      <DialogTrigger asChild disabled={disabled}>
        {triggerElement}
      </DialogTrigger>
      <DialogContent>
        <DialogHeader className="flex flex-row items-center justify-normal gap-4">
          {albumCoverThumbUrl && (
            <div className="h-20 w-[88px] shrink-0 rounded-xl">
              <img
                className="h-full w-full rounded-2xl object-cover"
                src={albumCoverThumbUrl}
                alt={album?.title}
              />
            </div>
          )}
          <div>
            <DialogTitle>
              <Trans>Share</Trans>
            </DialogTitle>
            <DialogDescription className="line-clamp-2 text-ellipsis">
              {album?.title || <Trans>No Title</Trans>}
            </DialogDescription>
          </div>
        </DialogHeader>
        <DialogBody className="grid">
          {isOwner && (
            <>
              <ToggleCard
                label={t`Share this album`}
                description={t`Viewable to anyone with the link`}
                icon={SvgLinkSimple}
                checked={isShared}
                loading={
                  shareAlbum.isPending ||
                  unshareAlbum.isPending ||
                  checkUnshareImpact.isPending
                }
                onCheckedChange={checked => {
                  if (checked) {
                    shareAlbum.mutate({
                      albumId,
                      accessLevel: ShareAccessLevel.WRITE,
                    })
                    return
                  }
                  checkUnshareImpact.mutate({
                    albumIdOrShareId: subscribersAlbumIdOrShareId ?? '',
                  })
                }}
              />
              <UnShareAlbumDialog
                open={confirmUnshareOpen}
                albumTitle={album?.title}
                subscribers={checkUnshareImpact.data?.subscribers}
                onClose={() => setConfirmUnshareOpen(false)}
                onUnShareAlbum={() => {
                  unshareAlbum.mutate({ albumId })
                  setConfirmUnshareOpen(false)
                }}
              />
              <AnimatePresence initial={false}>
                {isShared && (
                  <motion.div
                    className="grid"
                    initial={{ gridTemplateRows: '0fr', opacity: 0 }}
                    animate={{ gridTemplateRows: '1fr', opacity: 1 }}
                    exit={{ gridTemplateRows: '0fr', opacity: 0 }}
                    transition={{ duration: 0.2, ease: 'easeOut' }}
                  >
                    <div className="grid gap-5 overflow-hidden">
                      <Divider className="mt-5" />
                      <span className="label-sm">
                        <Trans>Link settings</Trans>
                      </span>
                      <ToggleCard
                        label={t`Anyone can add photos`}
                        description={t`Anyone with the link can contribute`}
                        icon={SvgUsersShared}
                        iconVariant="secondary"
                        checked={
                          album?.shareAccessLevel === ShareAccessLevel.WRITE
                        }
                        loading={shareAlbum.isPending}
                        onCheckedChange={checked =>
                          shareAlbum.mutate({
                            albumId,
                            accessLevel: checked
                              ? ShareAccessLevel.WRITE
                              : ShareAccessLevel.READ,
                          })
                        }
                      />
                      <CopyToClipboardCard
                        title={t`Share link`}
                        value={shareLink}
                      />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </>
          )}
          {!isOwner && (
            <CopyToClipboardCard title={t`Share link`} value={shareLink} />
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  )
}

function useShareAlbum(albumIdOrShareId?: string) {
  const transport = useTransport()
  const albumQueryKey = createConnectQueryKey({
    schema: getAlbum,
    input: { albumIdOrShareId },
    transport,
    cardinality: 'finite',
  })

  const subscribersQueryKey = createConnectQueryKey({
    schema: listSubscribers,
    cardinality: undefined,
  })

  const { data: album } = useQuery(
    getAlbum,
    { albumIdOrShareId },
    {
      enabled: Boolean(albumIdOrShareId),
      select: data => data.album,
    },
  )

  const { data: collection } = useQuery(
    getCollection,
    { collectionRef: album?.collectionRef },
    {
      enabled: Boolean(album?.collectionRef),
      select: res =>
        res.result.case === 'collection' ? res.result.value : undefined,
    },
  )

  const shareAlbum = useMutation(shareAlbumRequest, {
    throwOnError: false,
    onMutate: async input => {
      await queryClient.cancelQueries({ queryKey: albumQueryKey })

      const prevValue = queryClient.getQueryData(albumQueryKey)

      queryClient.setQueryData(albumQueryKey, old => {
        if (!old?.album) {
          return old
        }
        return {
          ...old,
          album: {
            ...old.album,
            shareId: old.album.shareId ?? OPTIMISTIC_SHARE_ID,
            shareAccessLevel: input.accessLevel,
          },
        }
      })

      return { prevValue }
    },

    onError: (_err, _value, context) => {
      if (context?.prevValue) {
        queryClient.setQueryData(albumQueryKey, context.prevValue)
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: albumQueryKey })
      queryClient.invalidateQueries({ queryKey: subscribersQueryKey })
    },
  })

  const unshareAlbum = useMutation(unshareAlbumRequest, {
    throwOnError: false,
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: albumQueryKey })
      const prevValue = queryClient.getQueryData(albumQueryKey)

      queryClient.setQueryData(albumQueryKey, old => {
        if (!old?.album) {
          return old
        }
        return {
          ...old,
          album: {
            ...old.album,
            shareId: undefined,
            shareAccessLevel: undefined,
          },
        }
      })

      return { prevValue }
    },

    onError: (_err, _value, context) => {
      if (context?.prevValue) {
        queryClient.setQueryData(albumQueryKey, context.prevValue)
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: albumQueryKey })
      queryClient.invalidateQueries({ queryKey: subscribersQueryKey })
    },
  })

  return {
    album,
    collection,
    shareAlbum,
    unshareAlbum,
  }
}
